import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useCarrier } from '../../../contexts/CarrierContext';
import { useAuth } from '../../../contexts/AuthContext';
import PANEL_CAPABILITIES from '../../../shared/panelCapabilities';
import * as rules from '../../../lib/madar/rules';
import { getDataVersion } from '../../../lib/madar/map';
const { validateRules } = require('../../../shared/madarRuleSchema');
const { normalizeFilter } = require('../../../shared/madarKinds');
const { UUID_PATTERN } = require('../../../shared/madarSchemaUtils');
const uuid = (value) => typeof value === 'string' && new RegExp(UUID_PATTERN).test(value);
export const WORKSPACES = Object.freeze(['network', 'route', 'connection', 'rules']);
export const MadarWorkspaceContext = createContext(null);
export const useMadarWorkspace = () => useContext(MadarWorkspaceContext);
let sequence = 0;
const initial = { assetId: null, hostId: null, endpointId: null, popId: null, firstHop: 'both', direction: 'any',
  routeMode: 'civil', connectionMode: 'board', filter: {}, view: null, editRequest: null };
function verifiedSettings(row, carrierId) {
  // Admission before schema recursion. No draft, foreign or unbounded response is policy.
  const stack = [[row?.settings, 0]]; let nodes = 0; let chars = 0;
  while (stack.length) {
    const [value, depth] = stack.pop();
    if (++nodes > 30000 || depth > 12) throw new Error('RESPONSE_INVALID');
    if (typeof value === 'string') { chars += value.length; if (value.length > 4096 || chars > 1048576) throw new Error('RESPONSE_INVALID'); }
    if (value && typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length > 512 || keys.some((key) => ['__proto__', 'prototype', 'constructor'].includes(key))) throw new Error('RESPONSE_INVALID');
      for (const key of keys) stack.push([value[key], depth + 1]);
    }
  }
  if (row?.status !== 'active' || row.carrier_id !== carrierId || row.schema_version !== 1 || !validateRules(row.settings).ok) throw new Error('RESPONSE_INVALID');
  return row.settings;
}
// POP targets Window itself. Chrome invokes those target listeners in registration
// order, even when capture=true. AdminPage imports this small module eagerly,
// before BrowserRouter creates its history listener. The dispatcher is inert
// outside an owned session; mounting/unmounting only attaches/detaches its guard.
let historyLeaveHandler = null;
if (typeof window !== 'undefined') window.addEventListener('popstate', (event) => historyLeaveHandler?.(event), true);
// Restore the accepted entry before asking the shared guard. The router never
// sees a rejected/pending departure, so the editor stays mounted.
function useHistoryLeaveGuard(confirmLeave, navLock) {
  const location = useLocation();
  const accepted = useRef(null); const leave = useRef(confirmLeave); leave.current = confirmLeave;
  useLayoutEffect(() => { accepted.current = window.history.state?.idx; }, [location.key]);
  useLayoutEffect(() => {
    let alive = true; let pending = null; let replay = null;
    const pop = (event) => {
      const index = event.state?.idx; const current = accepted.current;
      if (!Number.isInteger(index) || !Number.isInteger(current)) return;
      if (replay === index) { replay = null; accepted.current = index; return; }
      if (!pending && index === current) return;
      event.stopImmediatePropagation();
      if (index !== current) {
        if (!pending) { pending = { delta: index - current, started: false }; navLock.current = true; }
        window.history.go(current - index);
        return;
      }
      if (!pending || pending.started) return;
      const request = pending; request.started = true;
      Promise.resolve().then(() => leave.current()).then((allow) => {
        if (!alive || pending !== request) return;
        pending = null; navLock.current = false;
        if (allow) { replay = current + request.delta; window.history.go(request.delta); }
      }, () => { if (alive && pending === request) { pending = null; navLock.current = false; } });
    };
    historyLeaveHandler = pop;
    return () => { alive = false; if (historyLeaveHandler === pop) historyLeaveHandler = null; if (pending) navLock.current = false; };
  }, [navLock]);
}
// Retain only a validated bounded fit intent, never the trace or dossier payload.
function fitBoundsFor(geometry) {
  if (!geometry) return null;
  if (geometry.type === 'FeatureCollection' && (!Array.isArray(geometry.features) || geometry.features.length > 100)) return null;
  const shapes = geometry.type === 'FeatureCollection' ? geometry.features.map((feature) => feature?.geometry) : [geometry.type === 'Feature' ? geometry.geometry : geometry];
  let north = -90; let south = 90; let east = -180; let west = 180; let count = 0;
  const depths = { Point: 0, MultiPoint: 1, LineString: 1, MultiLineString: 2, Polygon: 2, MultiPolygon: 3 };
  const points = [];
  for (const shape of shapes) {
    if (!shape || !Object.hasOwn(depths, shape.type)) return null;
    points.push([shape.coordinates, depths[shape.type]]);
  }
  while (points.length) {
    const [point, depth] = points.pop();
    if (!Array.isArray(point) || ++count > 10000 || !point.length) return null;
    if (depth === 0) {
      if (point.length < 2 || point.length > 3) return null;
      const [lng, lat] = point;
      if (!Number.isFinite(lng) || !Number.isFinite(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) return null;
      north = Math.max(north, lat); south = Math.min(south, lat); east = Math.max(east, lng); west = Math.min(west, lng);
    } else {
      if (point.length > 10000 - count) return null;
      for (const child of point) points.push([child, depth - 1]);
    }
  }
  return south <= north ? { north, south, east, west } : null;
}
const emptySlot = () => null;
export function MadarWorkspaceProvider({ children, canWrite = true, role, renderEditSurface = emptySlot, renderLinkBudget = emptySlot }) {
  const { carrier } = useCarrier(); const { profile, session } = useAuth();
  const principal = session?.user?.id || profile?.id || 'session'; const effectiveRole = role || profile?.role;
  return <Session key={`${principal}:${profile?.status || ''}:${effectiveRole}:${carrier?.id || ''}`}
    {...{ carrier, role: effectiveRole, canWrite, renderEditSurface, renderLinkBudget }}>{children}</Session>;
}
function Session({ children, carrier, role, canWrite: writable, renderEditSurface, renderLinkBudget }) {
  const { confirmLeave } = useCarrier(); const location = useLocation(); const navigate = useNavigate();
  const allowed = !!carrier && PANEL_CAPABILITIES.canAccessPanelSection(role, 'madar');
  const [generation] = useState(() => ++sequence);
  const [state, setState] = useState(initial); const [revision, setRevision] = useState(0);
  const revisionClock = useRef(0);
  const bumpRevision = useCallback(() => { revisionClock.current += 1; setRevision(revisionClock.current); }, []);
  const [disabled, setDisabled] = useState(false); const [denied, setDenied] = useState(false);
  const [ruleData, setRuleData] = useState({ ruleState: allowed ? 'loading' : 'denied', settings: null, error: null });
  const [attempt, setAttempt] = useState(0); const [probing, setProbing] = useState(false);
  const [mapHandle, assignMap] = useState(null);
  const pendingFit = useRef(null); const fitSequence = useRef(0);
  // Read after an awaited leave confirmation, when the closure's map or workspace may be stale.
  const liveMap = useRef(null); const liveWorkspace = useRef(null);
  const lifetime = useRef(new AbortController()); const active = useRef(true); const navLock = useRef(false);
  useHistoryLeaveGuard(confirmLeave, navLock);
  const probe = useRef(null); const blocked = disabled || denied || !allowed;
  const requested = new URLSearchParams(location.search).getAll('workspace');
  const workspace = requested.length === 1 && WORKSPACES.includes(requested[0]) ? requested[0] : 'network';
  liveWorkspace.current = workspace;
  useEffect(() => { active.current = true; if (lifetime.current.signal.aborted) lifetime.current = new AbortController(); return () => { active.current = false; pendingFit.current = null; fitSequence.current += 1; lifetime.current.abort(); probe.current?.abort(); }; }, []);
  const observeError = useCallback((error) => {
    if (!active.current) return;
    if (error?.code === 'MADAR_DISABLED' || [401, 403].includes(error?.status)) {
      pendingFit.current = null; fitSequence.current += 1; lifetime.current.abort(); probe.current?.abort(); liveMap.current = null; assignMap(null); setState(initial);
      setRuleData({ ruleState: 'error', settings: null, error });
      if (error.code === 'MADAR_DISABLED') setDisabled(true); else setDenied(true);
    }
  }, []);
  useEffect(() => {
    if (!allowed || disabled || denied) return undefined;
    const controller = new AbortController();
    const parent = lifetime.current.signal; const abort = () => controller.abort();
    parent.addEventListener('abort', abort, { once: true });
    setRuleData({ ruleState: 'loading', settings: null, error: null });
    (async () => {
      const result = await rules.listRules({ signal: controller.signal });
      if (controller.signal.aborted) return;
      if (result?.carrier?.id !== carrier.id || !Array.isArray(result.versions) || result.versions.length > 512) throw new Error('RESPONSE_INVALID');
      const published = result.versions.filter((item) => item.status === 'active');
      if (published.length > 1 || published.some((item) => !uuid(item.id))) throw new Error('RESPONSE_INVALID');
      if (!published.length) { setRuleData({ ruleState: 'missing', settings: null, error: null }); return; }
      const row = await rules.getVersion(published[0].id, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (row?.id !== published[0].id) throw new Error('RESPONSE_INVALID');
      const settings = verifiedSettings(row, carrier.id);
      setRuleData({ ruleState: 'active', settings, error: null });
    })().catch((failure) => {
      if (controller.signal.aborted) return;
      const error = failure?.code ? failure : { code: 'RESPONSE_INVALID' };
      observeError(error); setRuleData({ ruleState: 'error', settings: null, error });
    });
    return () => { controller.abort(); parent.removeEventListener('abort', abort); };
  }, [allowed, carrier?.id, disabled, denied, attempt, observeError]);
  useEffect(() => {
    const refresh = () => { if (!lifetime.current.signal.aborted) { setAttempt((value) => value + 1); bumpRevision(); } };
    window.addEventListener('fttx:madar-rules-changed', refresh);
    return () => window.removeEventListener('fttx:madar-rules-changed', refresh);
  }, [bumpRevision]);
  const guarded = useCallback(async (change) => {
    if (navLock.current || !active.current) return false;
    navLock.current = true;
    try { const signal = lifetime.current.signal; if (signal.aborted || !await confirmLeave() || !active.current || signal.aborted) return false; change(); return true; }
    finally { navLock.current = false; }
  }, [confirmLeave]);
  const targetWorkspace = (name) => {
    const query = new URLSearchParams(location.search); query.set('workspace', name); query.delete('version');
    navigate({ pathname: '/admin/madar/map', search: `?${query}` });
  };
  const navigateWorkspace = (name) => WORKSPACES.includes(name) && guarded(() => targetWorkspace(name));
  const select = (changes, target) => guarded(() => {
    if (blocked) return;
    setState((value) => ({ ...value, editRequest: null, ...changes }));
    if (target) targetWorkspace(target);
  });
  const retryAvailability = async () => {
    if (!allowed || denied || probe.current) return false;
    const controller = new AbortController(); probe.current = controller; setProbing(true);
    try {
      if (PANEL_CAPABILITIES.canAccessPanelSection(role, 'madar.map')) {
        const result = await getDataVersion({ signal: controller.signal });
        if (!Number.isSafeInteger(result?.version) || result.version < 0) throw Object.assign(new Error('RESPONSE_INVALID'), { code: 'RESPONSE_INVALID' });
      } else {
        const result = await rules.listRules({ signal: controller.signal });
        if (result?.carrier?.id !== carrier.id || !Array.isArray(result.versions)) throw Object.assign(new Error('RESPONSE_INVALID'), { code: 'RESPONSE_INVALID' });
      }
      if (!active.current || controller.signal.aborted) return false;
      lifetime.current = new AbortController(); setDisabled(false); setAttempt((value) => value + 1); bumpRevision(); return true;
    } catch (error) { if (!controller.signal.aborted) observeError(error); return false; }
    finally { if (active.current) setProbing(false); probe.current = null; }
  };
  const setMapHandle = useCallback((map) => { liveMap.current = map; assignMap(() => map); }, []);
  useEffect(() => {
    if (workspace !== 'network' || blocked || !mapHandle?.fitBounds || !pendingFit.current) return;
    const bounds = pendingFit.current; pendingFit.current = null;
    mapHandle.fitBounds(bounds);
  }, [workspace, mapHandle, blocked]);
  return <MadarWorkspaceContext.Provider value={{ ...state, ...ruleData, workspace, revision, generation, disabled, denied,
    canWrite: allowed && writable && !blocked, probing, signal: lifetime.current.signal,
    navigateWorkspace, selectAsset: (value) => (value === null || uuid(value)) && select({ assetId: value, ...(value === null ? { hostId: null, endpointId: null, popId: null } : {}) }),
    openBoard: (hostId) => uuid(hostId) && select({ hostId, connectionMode: 'board' }, 'connection'),
    openTrace: (endpointId, { firstHop = 'both', direction = 'any' } = {}) => uuid(endpointId) && select({ endpointId,
      firstHop: ['external', 'internal', 'both'].includes(firstHop) ? firstHop : 'both',
      direction: ['any', 'upstream', 'downstream'].includes(direction) ? direction : 'any', routeMode: 'trace' }, 'route'),
    openRoom: (popId) => uuid(popId) && select({ popId, connectionMode: 'room' }, 'connection'),
    setRouteMode: (routeMode) => ['civil', 'trace'].includes(routeMode) && select({ routeMode }),
    setConnectionMode: (connectionMode) => ['board', 'room'].includes(connectionMode) && select({ connectionMode }),
    fitGeometry: (geometry) => {
      const request = ++fitSequence.current; pendingFit.current = null;
      const bounds = fitBoundsFor(geometry);
      if (blocked || !bounds) return false;
      return guarded(() => {
        if (request !== fitSequence.current) return;
        const onMap = liveWorkspace.current === 'network';
        if (onMap && liveMap.current?.fitBounds) { liveMap.current.fitBounds(bounds); return; }
        // No visible map yet: the registration effect applies it to the next map, never a detached one.
        pendingFit.current = bounds; if (!onMap) targetWorkspace('network');
      });
    },
    startMove: (assetId) => uuid(assetId) && writable && !blocked && ruleData.ruleState === 'active' && select({ editRequest: { kind: 'move', assetId } }, 'network'),
    setFilter: (filter) => {
      if (!filter || typeof filter !== 'object' || Object.keys(filter).length > 5) return false;
      if (Object.values(filter).some((value) => Array.isArray(value) && value.length > 64)) return false;
      try { return select({ filter: normalizeFilter(filter) }); } catch { return false; }
    },
    setView: (view) => {
      if (view === null) return select({ view: null });
      if (!view || !Number.isFinite(view.centre?.lat) || !Number.isFinite(view.centre?.lng) || Math.abs(view.centre.lat) > 90 || Math.abs(view.centre.lng) > 180
        || !Number.isInteger(view.zoom) || view.zoom < 0 || view.zoom > 22 || !['roadmap', 'satellite', 'osm', 'traffic', 'none'].includes(view.base_map)) return false;
      return select({ view: { centre: { lat: view.centre.lat, lng: view.centre.lng }, zoom: view.zoom, base_map: view.base_map } });
    },
    setEditRequest: (request) => {
      if (request === null) { setState((value) => ({ ...value, editRequest: null })); return; }
      if (blocked || !writable || ruleData.ruleState !== 'active' || !['create', 'move', 'bulk', 'delete'].includes(request?.kind)) return;
      if (request.assetId && !uuid(request.assetId)) return;
      if (request.ids && (!Array.isArray(request.ids) || request.ids.length > 500 || !request.ids.every(uuid))) return;
      select({ editRequest: { kind: request.kind, ...(request.assetId ? { assetId: request.assetId } : {}), ...(request.ids ? { ids: [...request.ids] } : {}) } });
    }, mapHandle, setMapHandle, notifyMutation: (options) => { if (!blocked && (options?.sinceRevision === undefined || options.sinceRevision === revisionClock.current)) bumpRevision(); },
    observeError, retryAvailability, renderEditSurface, renderLinkBudget,
  }}>{children}</MadarWorkspaceContext.Provider>;
}
